import {
  CONCEPTUAL_TAGS,
  filterConceptualTags,
  genreForAuthor,
} from '@/constants/conceptualTags';

/**
 * Works out conceptual nodes for a book WITHOUT calling the AI.
 *
 * The Log Signal form auto-picks nodes through the `ai-tag-suggestions` edge
 * function. That function is a paid gateway call, so it can be unavailable
 * (rate limit, exhausted credits, cold start, offline) — and when it failed the
 * form simply showed nothing, which is indistinguishable from "this book has no
 * obvious nodes". This gives the form something honest to fall back to.
 *
 * DELIBERATELY CONSERVATIVE. Suggestions the user must then un-pick are worse
 * than no suggestion, so the patterns below are distinctive phrases rather than
 * common words: "wasteland" and not "ruins", "time loop" and not "time". An
 * earlier keyword sweep over this catalogue mis-classified Heinlein, Clarke and
 * Wells precisely by matching generic genre words, so nothing here fires on a
 * bare "space", "future", "war" or "alien".
 *
 * Everything it returns is a suggestion the user can remove.
 */

/** Distinctive phrases per node. Matched case-insensitively against the
 *  description and the provider's category strings. */
const NODE_PATTERNS: ReadonlyArray<[string, RegExp]> = [
  ['Cyberpunk', /\bcyberpunk\b|\bcyberspace\b|\bhackers?\b|\bblack ice\b/i],
  ['Post-Cyberpunk', /\bpost-?cyberpunk\b/i],
  ['Space Opera', /\bspace opera\b/i],
  ['Hard Science Fiction', /\bhard (?:science fiction|sf|sci-?fi)\b/i],
  ['Biopunk', /\bbiopunk\b|\bgenetic engineering\b|\bgene-?splic/i],
  ['Golden Age', /\bgolden age of (?:science fiction|sf)\b/i],
  ['Block Universe Compatible', /\bblock universe\b/i],
  ['Time Dilation', /\btime dilation\b|\brelativistic\b/i],
  ['Chrono Loops', /\btime loops?\b|\btemporal paradox\b|\bcausal loop\b/i],
  ['Technological Shamanism', /\btechno-?shaman|\btechnological shamanism\b/i],
  ['Utopian Collapse', /\butopian?\b/i],
  ['Mega-Corporate Systems', /\bmega-?corp|\bzaibatsu\b|\bcorporate (?:state|rule|overlords?)\b/i],
  ['Off-Earth Civilisations', /\bgeneration ship\b|\bspace (?:colony|station|habitat)\b|\b(?:lunar|martian) colony\b/i],
  ['Dystopian Systems', /\bdystopian?\b|\btotalitarian\b|\bsurveillance state\b/i],
  ['Nonlinear Structure', /\bnon-?linear narrative\b|\bfragmented narrative\b/i],
  ['Dream Logic', /\bdream-?like\b|\bsurreal\b|\boneiric\b/i],
  ['Archive-Based', /\bepistolary\b|\btold through (?:documents|letters|logs|records)\b/i],
  ['Memory Distortion', /\b(?:false|implanted|erased) memor|\bamnesia\b/i],
  ['Cybernetic Enhancement', /\bcyborgs?\b|\bcybernetic\b|\bprosthe|\baugmented humans?\b/i],
  ['Quantum Consciousness', /\bquantum (?:consciousness|mind)\b/i],
  ['Neural Interface', /\bneural interface\b|\bbrain-?computer\b|\bmind upload/i],
  ['Posthuman Evolution', /\bpost-?human\b|\btrans-?human\b/i],
  ['Post-Apocalyptic', /\bpost-?apocalyp|\bwasteland\b|\bafter the collapse\b/i],
  ['First Contact', /\bfirst contact\b/i],
  ['Science Fantasy', /\bscience fantasy\b/i],
  ['Alternate History', /\baltern(?:ate|ative) history\b/i],
  ['Military SF', /\bmilitary (?:science fiction|sf)\b|\bspace marines?\b/i],
  ['Feminist SF', /\bfeminist\b/i],
  ['Africanfuturism', /\bafric[ao]futurism\b|\bafrofuturism\b/i],
  ['New Wave', /\bnew wave\b/i],
  ['Dying Earth', /\bdying earth\b/i],
  ['Galactic Empire', /\bgalactic empire\b/i],
  ['Telepathy', /\btelepath|\bpsychic\b|\besper\b/i],
  ['Immortality', /\bimmortal|\beternal life\b|\bradical longevity\b/i],
  ['Artificial Intelligence', /\bartificial intelligence\b|\bsentient (?:machine|computer|robot|ai)\b|\bmachine mind\b/i],
  ['Survival', /\bsurvival\b|\bstruggle to survive\b/i],
];

export interface InferredNode {
  name: string;
  /** Shown to the user so a pick is never unexplained. */
  reason: string;
}

export interface InferInput {
  author?: string | null;
  description?: string | null;
  /** Provider categories, e.g. Google Books "Fiction / Science Fiction / Cyberpunk". */
  categories?: string[] | null;
}

/** How many nodes to offer. Three is what the AI path applies, so the fallback
 *  cannot suddenly flood the form with more than the user is used to. */
export const MAX_INFERRED = 3;

export function inferConceptualNodes(input: InferInput): InferredNode[] {
  const picks: InferredNode[] = [];
  const taken = new Set<string>();

  const add = (name: string, reason: string) => {
    if (taken.has(name) || picks.length >= MAX_INFERRED) return;
    // Guard against a pattern naming a node that has since been renamed or
    // removed from the vocabulary.
    if (!(CONCEPTUAL_TAGS as readonly string[]).includes(name)) return;
    taken.add(name);
    picks.push({ name, reason });
  };

  // 1. The curated author map first — it is hand-maintained, so it beats any
  //    amount of prose matching for authors it knows.
  const byAuthor = input.author ? genreForAuthor(input.author) : null;
  if (byAuthor) add(byAuthor, `${input.author} is catalogued under ${byAuthor}`);

  // 2. Then distinctive phrases in the blurb and the provider's categories.
  const haystack = [input.description ?? '', ...(input.categories ?? [])]
    .join(' \n ')
    .slice(0, 4000); // blurbs can be long; this is plenty and bounds the work

  if (haystack.trim()) {
    for (const [name, pattern] of NODE_PATTERNS) {
      if (picks.length >= MAX_INFERRED) break;
      const m = haystack.match(pattern);
      if (m) add(name, `the description mentions “${m[0].toLowerCase()}”`);
    }
  }

  // Canonicalise through the same filter the rest of the app uses, so a pick
  // here is always a tag the map and pattern recognition will recognise.
  const canonical = filterConceptualTags(picks.map((p) => p.name));
  return canonical
    .map((name) => picks.find((p) => p.name === name))
    .filter((p): p is InferredNode => Boolean(p));
}
